import get from "lodash/get";
import type { z } from "zod";
import RadioGroup from "~/components/formElements/inputs/radio/RadioGroup";
import StandaloneCheckbox from "~/components/formElements/inputs/standaloneCheckbox/StandaloneCheckbox";
import type { StrapiFormComponent } from "~/services/cms/models/formElements/StrapiFormComponent";
import Checkbox from "../inputs/checkbox/Checkbox";
import Select from "../inputs/select/Select";
import Tile from "../inputs/tile/Tile";
import { sortSchemaOptionsByFormComponents } from "./sortSchemaOptionsByFormComponents";

type ZodEnum = z.ZodEnum<Record<string, string>>;

export const isZodEnum = (fieldSchema: z.ZodType): fieldSchema is ZodEnum =>
  fieldSchema.def.type === "enum";

export function renderZodEnum(
  schema: ZodEnum,
  fieldName: string,
  matchingElement?: StrapiFormComponent,
) {
  const label = get(matchingElement, "label");
  const suffix = get(matchingElement, "suffix");
  const errorMessages = get(matchingElement, "errorMessages");

  const sortedOptions = sortSchemaOptionsByFormComponents(
    schema,
    matchingElement,
  );

  let options = sortedOptions.map((value) => ({ value, text: value }));
  switch (matchingElement?.__component) {
    case "form-elements.checkbox":
      return (
        <Checkbox
          key={fieldName}
          name={fieldName}
          label={label}
          required={matchingElement.required}
          errorMessage={matchingElement.errorMessage}
          suffix={suffix}
        />
      );
    case "form-elements.standalone-checkbox":
      return (
        <StandaloneCheckbox
          key={fieldName}
          name={fieldName}
          label={label}
          text={matchingElement.text}
          required={matchingElement.required}
          errorMessage={matchingElement.errorMessage}
        />
      );
    case "form-elements.tile-group": {
      const cmsOptions = get(matchingElement, "options");
      const cmsObject = Object.fromEntries(
        cmsOptions?.map(({ value, ...rest }) => [value, rest]),
      );
      return (
        <Tile
          key={fieldName}
          name={fieldName}
          useTwoColumns={matchingElement.useTwoColumns}
          label={label}
          errorMessages={errorMessages}
          options={options.map(({ value }) => ({
            value,
            description: cmsObject[value]?.description,
            image: cmsObject[value]?.image,
            title: cmsObject[value]?.title ?? value,
          }))}
        />
      );
    }
    case "form-elements.dropdown": {
      const cmsOptions = get(matchingElement, "options");
      const size = get(matchingElement, "size");
      const cmsObject = Object.fromEntries(
        cmsOptions?.map(({ value, ...rest }) => [value, rest]),
      );
      return (
        <Select
          name={fieldName}
          key={fieldName}
          label={label}
          options={options.map(({ value, text }) => ({
            value,
            text: cmsObject[value]?.text ?? text,
            preSelected: cmsObject[value]?.preSelected,
          }))}
          errorMessages={errorMessages}
          size={size}
          suffix={suffix}
        />
      );
    }
    case "form-elements.select":
      {
        const cmsOptions = get(matchingElement, "options");
        const cmsObject = Object.fromEntries(
          cmsOptions?.map(({ value, ...rest }) => [value, rest]),
        );
        options = options.map(({ value, text }) => ({
          value,
          text: cmsObject[value]?.text ?? text,
        }));
      }
      return (
        <RadioGroup
          key={fieldName}
          name={fieldName}
          label={label}
          altLabel={get(matchingElement, "altLabel")}
          errorMessages={errorMessages}
          options={options}
        />
      );
    default:
      return (
        <RadioGroup
          key={fieldName}
          name={fieldName}
          label={label ?? fieldName} // Fallback to fieldName without matchingElement
          altLabel={get(matchingElement, "altLabel")}
          errorMessages={errorMessages}
          options={options}
        />
      );
  }
}
